/**
 * What the page believes about the world, and the one function that changes it.
 *
 * This is deliberately a pure reducer over the frames in `docs/api.md` § 1 rather than a pile of
 * setters: the transport, the REST fallback and the tests all feed the same `applyMessage`, so
 * there is exactly one place where a wire field becomes a rendered number. F12 grows this into
 * the full store with optimistic counters, the per-square state machine and the paced event
 * queue; what is here is the subset the home page needs to stop lying — status, zombie count,
 * the global click counter and the Codex reset counter.
 *
 * Three rules are not negotiable, and each one has already been a bug in some other client of
 * this contract:
 *
 *   1. **Nothing before `hello`.** A `tick` that arrives on a socket whose `hello` was lost would
 *      otherwise apply absolute counters over a state that has no providers in it at all.
 *   2. **Absent and null are the same thing.** A peaceful provider omits `z`/`c`/`bc`/`sid`; a
 *      client that reads `snapshot.z` as a number gets `undefined` and renders `NaN` zombies.
 *   3. **An unchanged frame returns the same object.** Ticks arrive at 2 Hz whether or not they
 *      concern a square, and both React and the scene decide whether to do work by identity.
 *
 * On top of the server's numbers sits the optimistic layer, which exists because the counter has
 * to move on the press and the press is up to 200 ms away from the wire. See `Counters` below;
 * its two rules — the server is authoritative, and the display never counts down — are what make
 * a shared counter worth hammering.
 */

import { QUEUE_CAP } from '@/net/clickBatcher'
import { DPS as GAME_DPS, SPAWN_PER_CLICK as GAME_SPAWN_PER_CLICK } from '@/net/gameDefaults'
import {
  PROVIDER_IDS,
  isProviderId,
  type ClickTarget,
  type Incident,
  type ProviderId,
  type ProviderSnapshot,
  type ResetRecord,
  type ServerMsg,
  type TransportStatus,
  type WireWalls,
} from '@/net/transport'

/**
 * The v2 game for one square, as the page holds it (`docs/api.md` § 1.2, R-116).
 *
 * `hp` is the server's last quantised value, never an interpolated one: the drain between ticks is
 * a *display* concern (`ui/drain.ts`) and lives outside the store, so a frame that arrives always
 * snaps to the truth and two subscribers can never disagree about what the wall is at.
 */
export interface WallsState {
  /** The unbounded v2 count. Per siege, never reset by a fallen wall. */
  z: number
  /** Fallen walls, 0–3. Three is the overrun. */
  w: number
  /** Current wall's remaining health, 0–1000. Zero when `w` is 3. */
  hp: number
  /** ISO fall time per fallen wall, in order. */
  fell: readonly string[]
  /** Outbound post URL per fallen wall, `null` where nothing was posted. */
  post: readonly (string | null)[]
}

/**
 * Zombies one accepted click spawns in v2 (`Mha__Game__SpawnPerClick`; R-113).
 *
 * The one v2 number the client has to know before the server tells it: the odometer must move on
 * the press, and the press is up to 200 ms ahead of the ack that carries `zw`. Not on the wire
 * today, so this is the documented default and nothing else — `ack.zw` and every tick snap the
 * count to the server's own arithmetic, so a server tuned differently costs one press of lead,
 * never a wrong number.
 */
export const SPAWN_PER_CLICK: number = GAME_SPAWN_PER_CLICK

/** `Mha__Game__Dps` until the server says otherwise (R-113). Read from `hello` when present. */
export const DEFAULT_DPS: number = GAME_DPS

/** The gate's health as the wire spells it: 0–1000 (`gh`). Full is what "nothing reported" means. */
export const GATE_FULL = 1000

/** What a besieged square's walls look like before the server has said anything about them. */
export const INTACT_WALLS: WallsState = { z: 0, w: 0, hp: 1000, fell: [], post: [] }

export interface SquareState {
  /** The server's own two states. Client-only phases (breaching, allclear) arrive with F17. */
  phase: 'peaceful' | 'siege'
  sid: string | null
  startedAt: string | null
  /**
   * When this provider was last fine again, or `null` if it never has been within the window the
   * server keeps. The drawer's answer to "how long has this been quiet?" — which is the only
   * interesting thing a peaceful square has to say.
   */
  lastEndedAt: string | null
  /** The provider's status page says it is down, which is not quite the same as "under siege". */
  down: boolean
  incidents: readonly Incident[]
  /** Zombies at this wall right now. Always 0 when peaceful. */
  zombies: number
  /** Clicks during the current siege. */
  clicks: number
  breaches: number
  /**
   * The gate's health, 0–1000 (`gh` on the wire). Full when peaceful, before a siege's first tick,
   * and on a server from before the gate had health — "nothing reported" is an intact gate.
   */
  gateHp: number
  /**
   * The v2 game, or `null` when the server has not reported it — a peaceful square, or a live
   * server from before B24. The v2 page draws `null` as intact walls and an empty horde, which is
   * the honest picture of "nothing has been reported", and the v1 page never reads it.
   */
  walls: WallsState | null
}

/**
 * The optimistic layer over the v2 count, one per square.
 *
 * The same two rules as `Counters` — the server is authoritative and the number never counts
 * down — for the same reason: a horde that shrinks when the ack lands tells the visitor their
 * press was a lie. Kept beside `Counters` rather than inside it because the v1 counters are what
 * `/` renders, and this page's rule is that `/` does not change.
 */
export interface HordeLead {
  /** Spawns believed but not yet seen in any server `walls.z`. */
  readonly unacked: number
  /** What the v2 odometer renders. */
  readonly shown: number
}

/**
 * The five numbers a visitor can watch move, and the only five a press can change: the lifetime
 * global total, each square's clicks this siege, and the Codex reset sign.
 */
export const COUNTER_KEYS = ['all', 'openai', 'anthropic', 'google', 'reset'] as const
export type CounterKey = (typeof COUNTER_KEYS)[number]

/**
 * The optimistic layer over the server's counters.
 *
 * `shown = server + unacked` is the whole idea, plus one rule that overrides it: **the number
 * never counts down.** A shared counter that rewinds tells every visitor their clicks were a lie,
 * which is the one thing this page cannot afford to say — so `shown` is a ratchet, and a
 * reconciliation that would lower it leaves it standing still until the world catches up instead.
 *
 * The honest consequence, written down because it is a real difference and not a rounding error:
 * a tab that has been hammered shows a *higher* number than a fresh load will. Some of its clicks
 * were clipped by the server's rate budget and are gone (`docs/api.md` § 1.1: 25/s per socket,
 * 100/s per address), but they were already on screen. The lead is bounded by `MAX_LEAD` and
 * closes on its own, because everyone else's clicks keep raising the server's number underneath
 * it; a reload always shows the truth.
 *
 * The exception to the ratchet is a counter that genuinely restarted — a new siege, a Codex
 * reset, an admin repair. Those arrive as a server value *below* the previous one, and there the
 * display follows the server down, because the thing being counted is not the same thing any more.
 */
export interface Counters {
  /** Presses not yet observed in any server number. */
  readonly unacked: Readonly<Record<CounterKey, number>>
  /** What the page renders. Monotonic within a counter's life. */
  readonly shown: Readonly<Record<CounterKey, number>>
  /** `Date.now()` of the last press, for the decay below. */
  readonly lastPressAt: number
  /**
   * Presses this tab did **not** make, counted for ever upwards.
   *
   * A tick's increment is everyone's: this tab's presses that the server has now seen, plus every
   * other visitor's. Subtracting the ones this tab is owed leaves the rest, which is the only
   * evidence a browser has that somebody else is out there pressing the same button. `MustHaveButton`
   * watches it and throws the same particle it throws for a local press, so a quiet page still
   * feels inhabited.
   *
   * Monotonic rather than a per-tick delta so a consumer that misses a render still spawns the
   * right number: it compares against what it last saw, not against zero.
   */
  readonly remote: Readonly<Record<CounterKey, number>>
}

/**
 * How far the display may run ahead of the server, and how long an unobserved press is believed.
 *
 * The lead is the click queue's own cap (`docs/api.md` § 1.3) for the same reason it exists
 * there: past 200 unsent clicks a tab is no longer reporting, it is inventing. The decay is the
 * five seconds `docs/architecture/frontend.md` specifies — long enough that an ordinary round
 * trip never trips it, short enough that a batch lost to a dropped socket cannot strand the
 * display in a permanent overcount.
 */
const MAX_LEAD = QUEUE_CAP
export const UNACKED_DECAY_MS = 5_000

const ZERO_COUNTS: Record<CounterKey, number> = {
  all: 0,
  openai: 0,
  anthropic: 0,
  google: 0,
  reset: 0,
}

export interface LiveState {
  connection: TransportStatus
  /** Until this is true the page shows "waiting", never a zero — a zero that never moves reads as broken. */
  helloReceived: boolean
  /**
   * The server's state version from `hello` (`docs/api.md` § 1.2), and the only use it has on
   * this client: the `?v=` on `/og.png`, so the picture somebody copies into a post is the state
   * they were looking at rather than whatever a CDN cached a minute ago. Ticks do not carry it,
   * which is fine — `v` is a cache-buster the server ignores, and a counter moving is not a new
   * card.
   */
  ver: number
  /** Lifetime global "MUST HAVE AI" clicks. */
  all: number
  /** Current "MUST HAVE RESET" counter. */
  resetClicks: number
  lastReset: ResetRecord | null
  squares: Readonly<Record<ProviderId, SquareState>>
  counters: Counters
  /** Damage per zombie per second, for the between-tick drain. `hello` may carry it; default 1. */
  dps: number
  horde: Readonly<Record<ProviderId, HordeLead>>
}

const NO_LEAD: HordeLead = { unacked: 0, shown: 0 }
const ZERO_HORDE: Record<ProviderId, HordeLead> = {
  openai: NO_LEAD,
  anthropic: NO_LEAD,
  google: NO_LEAD,
}

const PEACEFUL: SquareState = {
  phase: 'peaceful',
  sid: null,
  startedAt: null,
  lastEndedAt: null,
  down: false,
  incidents: [],
  zombies: 0,
  clicks: 0,
  breaches: 0,
  gateHp: GATE_FULL,
  walls: null,
}

export function initialLiveState(): LiveState {
  return {
    connection: 'connecting',
    helloReceived: false,
    ver: 0,
    all: 0,
    resetClicks: 0,
    lastReset: null,
    squares: { openai: PEACEFUL, anthropic: PEACEFUL, google: PEACEFUL },
    counters: { unacked: ZERO_COUNTS, shown: ZERO_COUNTS, lastPressAt: 0, remote: ZERO_COUNTS },
    dps: DEFAULT_DPS,
    horde: ZERO_HORDE,
  }
}

/** The server's own value for a counter, wherever it happens to live in the state. */
function serverCount(state: LiveState, key: CounterKey): number {
  if (key === 'all') return state.all
  if (key === 'reset') return state.resetClicks
  return state.squares[key].clicks
}

/** Which counters one press moves. A reset click is not an "AI click" and never joins `all`. */
function keysFor(target: ClickTarget): readonly CounterKey[] {
  return target === 'reset'
    ? RESET_KEYS
    : target === 'openai'
      ? OPENAI_KEYS
      : target === 'anthropic'
        ? ANTHROPIC_KEYS
        : GOOGLE_KEYS
}

const RESET_KEYS: readonly CounterKey[] = ['reset']
const OPENAI_KEYS: readonly CounterKey[] = ['openai', 'all']
const ANTHROPIC_KEYS: readonly CounterKey[] = ['anthropic', 'all']
const GOOGLE_KEYS: readonly CounterKey[] = ['google', 'all']

/**
 * One press, believed immediately.
 *
 * The display moves by one whatever the wire does, unless it is already `MAX_LEAD` ahead of the
 * server — at which point the press still animates, still leaves for the server and still counts;
 * it is only the number that stops running away.
 */
export function applyPress(state: LiveState, target: ClickTarget, now: number): LiveState {
  const unacked = { ...state.counters.unacked }
  const shown = { ...state.counters.shown }
  for (const key of keysFor(target)) {
    unacked[key] = Math.min(MAX_LEAD, unacked[key] + 1)
    if (shown[key] + 1 - serverCount(state, key) <= MAX_LEAD) shown[key] = shown[key] + 1
  }
  const next = { ...state, counters: { unacked, shown, lastPressAt: now, remote: state.counters.remote } }
  if (target === 'reset') return next
  // The v2 odometer moves by `SpawnPerClick` on the same press, under the same lead cap measured
  // in presses — so a tab that has run away on `/` has run away by the same amount on `/v2`.
  const lead = state.horde[target]
  const serverZ = state.squares[target].walls?.z ?? 0
  const maxLead = MAX_LEAD * SPAWN_PER_CLICK
  const bumped: HordeLead = {
    unacked: Math.min(maxLead, lead.unacked + SPAWN_PER_CLICK),
    shown:
      lead.shown + SPAWN_PER_CLICK - serverZ <= maxLead ? lead.shown + SPAWN_PER_CLICK : lead.shown,
  }
  return { ...next, horde: { ...state.horde, [target]: bumped } }
}

/**
 * Forget presses the server never confirmed.
 *
 * Without this a single lost batch would leave `unacked` standing for the life of the tab, and
 * every later reconciliation would subtract from a number that no longer means anything. Because
 * `shown` is a ratchet, forgetting them is invisible: the display holds where it is instead of
 * dropping, and the next server value it is allowed to follow is one that is genuinely higher.
 */
export function decayUnacked(state: LiveState, now: number): LiveState {
  if (now - state.counters.lastPressAt < UNACKED_DECAY_MS) return state
  const { unacked } = state.counters
  const hordeIdle = PROVIDER_IDS.every((id) => state.horde[id].unacked === 0)
  if (COUNTER_KEYS.every((key) => unacked[key] === 0) && hordeIdle) return state
  let horde = state.horde
  if (!hordeIdle) {
    const cleared = { ...state.horde }
    for (const id of PROVIDER_IDS) {
      if (cleared[id].unacked !== 0) cleared[id] = { unacked: 0, shown: cleared[id].shown }
    }
    horde = cleared
  }
  return { ...state, counters: { ...state.counters, unacked: ZERO_COUNTS }, horde }
}

/**
 * Fold the server's new numbers into the optimistic layer.
 *
 * `delta` is what the server moved, not what an ack claimed to accept. Both a `tick` and an ack
 * carrying `r` are the same evidence — a counter we can see — and using `ack.n` here as well
 * would subtract the same clicks twice, because the tick that follows contains them too.
 */
function reconcile(prev: LiveState, next: LiveState, live: boolean): Counters {
  const counters = prev.counters
  let unacked: Record<CounterKey, number> | null = null
  let shown: Record<CounterKey, number> | null = null
  let remote: Record<CounterKey, number> | null = null

  for (const key of COUNTER_KEYS) {
    const from = serverCount(prev, key)
    const to = serverCount(next, key)
    // A siege that ends and one that begins both restart a square's counter at zero, and when
    // the old siege had no clicks the number alone cannot say so — only the `sid` can. Without
    // this the presses from the last siege would be a floor under the new one.
    const restarted =
      key !== 'all' && key !== 'reset' && prev.squares[key].sid !== next.squares[key].sid
    if (to === from && !restarted) continue

    if (to < from || restarted) {
      // Not a rewind of the same counter: a new siege, a Codex reset, an admin repair. The old
      // total is not a floor for a number that has started again.
      if (counters.unacked[key] !== 0) (unacked ??= { ...counters.unacked })[key] = 0
      if (counters.shown[key] !== to) (shown ??= { ...counters.shown })[key] = to
      continue
    }

    const held = Math.max(0, counters.unacked[key] - (to - from))
    if (held !== counters.unacked[key]) (unacked ??= { ...counters.unacked })[key] = held
    const target = to + held
    if (target > counters.shown[key]) (shown ??= { ...counters.shown })[key] = target

    // Whatever the server counted that this tab cannot claim was somebody else. Ticks only: a
    // `hello` carries the whole world's total, and crediting 98 765 presses to "other visitors"
    // would fire a firework on every reconnect.
    if (live) {
      const mine = counters.unacked[key] - held
      const others = to - from - mine
      if (others > 0) (remote ??= { ...counters.remote })[key] = counters.remote[key] + others
    }
  }

  if (unacked === null && shown === null && remote === null) return counters
  return {
    unacked: unacked ?? counters.unacked,
    shown: shown ?? counters.shown,
    lastPressAt: counters.lastPressAt,
    remote: remote ?? counters.remote,
  }
}

/**
 * The same fold for the v2 count. `walls.z` is the evidence, wherever it arrived — a tick, an
 * `ack.zw`, a `wall` event — and an ack's `n` is deliberately not used for the same reason it is
 * not used above: the tick that follows contains those spawns too, and subtracting twice is how a
 * press ends up counted once and then uncounted.
 */
function reconcileHorde(prev: LiveState, next: LiveState): LiveState['horde'] {
  let horde: Record<ProviderId, HordeLead> | null = null
  for (const id of PROVIDER_IDS) {
    const from = prev.squares[id].walls?.z ?? 0
    const to = next.squares[id].walls?.z ?? 0
    const restarted = prev.squares[id].sid !== next.squares[id].sid
    if (to === from && !restarted) continue
    const lead = prev.horde[id]
    let after: HordeLead
    if (to < from || restarted) {
      after = { unacked: 0, shown: to }
    } else {
      const held = Math.max(0, lead.unacked - (to - from))
      after = { unacked: held, shown: Math.max(lead.shown, to + held) }
    }
    if (after.unacked === lead.unacked && after.shown === lead.shown) continue
    ;(horde ??= { ...prev.horde })[id] = after
  }
  return horde ?? prev.horde
}

/** `undefined`, `null` and a non-number all mean "the server did not say"; say zero instead. */
function count(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

/**
 * The last reset, with its one optional field normalised to `null`.
 *
 * `clicksTaken` is the exception to the rule two lines above: zero is not the right reading of
 * "the server did not say", because every reset in the imported archive predates the counter and
 * carries no click count at all — the server drops the property rather than sending a null. A
 * consumer that reads it as a number renders `NaN clicks` on the deployed site, which is exactly
 * what the hut panel did until this normalisation existed.
 */
function resetRecord(raw: ResetRecord | null | undefined): ResetRecord | null {
  if (!raw || typeof raw.id !== 'string') return null
  const clicksTaken = raw.clicksTaken
  const known = typeof clicksTaken === 'number' && Number.isFinite(clicksTaken)
  return known ? raw : { ...raw, clicksTaken: null }
}

/**
 * `walls` off the wire, or what was known before when the frame does not carry it.
 *
 * Every number is clamped to what the contract promises — `w` to 0–3, `hp` to 0–1000 — because
 * a bar drawn from an unclamped value is a bar drawn outside its box, and the only way `hp` can
 * be 1200 is a server bug this page should survive rather than render.
 */
function wallsFrom(
  raw: WireWalls | null | undefined,
  previous: WallsState | null,
): WallsState | null {
  if (!raw || typeof raw !== 'object') return previous
  const w = Math.max(0, Math.min(3, Math.trunc(count(raw.w))))
  const next: WallsState = {
    z: Math.max(0, Math.trunc(count(raw.z))),
    w,
    hp: w >= 3 ? 0 : Math.max(0, Math.min(1000, Math.trunc(count(raw.hp)))),
    // A tick carries only the three moving numbers; `fell` and `post` are kept from the last
    // frame that had them (docs/api.md § 1.2).
    fell: Array.isArray(raw.fell)
      ? raw.fell.filter((at): at is string => typeof at === 'string')
      : (previous?.fell ?? []),
    post: Array.isArray(raw.post)
      ? raw.post.map((url) => (typeof url === 'string' ? url : null))
      : (previous?.post ?? []),
  }
  return previous !== null && sameWalls(previous, next) ? previous : next
}

function sameWalls(a: WallsState | null, b: WallsState | null): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  return (
    a.z === b.z &&
    a.w === b.w &&
    a.hp === b.hp &&
    a.fell.length === b.fell.length &&
    a.post.length === b.post.length
  )
}

function squareFromSnapshot(snapshot: ProviderSnapshot): SquareState {
  const siege = snapshot.state === 'siege'
  return {
    phase: siege ? 'siege' : 'peaceful',
    sid: snapshot.sid ?? null,
    startedAt: snapshot.startedAt ?? null,
    lastEndedAt: snapshot.lastEndedAt ?? null,
    down: snapshot.down === true,
    incidents: Array.isArray(snapshot.incidents) ? snapshot.incidents : [],
    // A peaceful square has no crowd even if a stale `z` rode along with the frame.
    zombies: siege ? count(snapshot.z) : 0,
    clicks: siege ? count(snapshot.c) : 0,
    breaches: siege ? count(snapshot.bc) : 0,
    gateHp: siege ? gate(snapshot.gh, GATE_FULL) : GATE_FULL,
    walls: siege ? wallsFrom(snapshot.walls, null) : null,
  }
}

function gate(value: number | null | undefined, previous: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return previous
  return Math.max(0, Math.min(GATE_FULL, Math.trunc(value)))
}

function sameSquare(a: SquareState, b: SquareState): boolean {
  return (
    a.phase === b.phase &&
    a.sid === b.sid &&
    a.startedAt === b.startedAt &&
    a.lastEndedAt === b.lastEndedAt &&
    a.down === b.down &&
    a.zombies === b.zombies &&
    a.clicks === b.clicks &&
    a.breaches === b.breaches &&
    a.gateHp === b.gateHp &&
    sameWalls(a.walls, b.walls) &&
    sameIncidents(a.incidents, b.incidents)
  )
}

function sameIncidents(a: readonly Incident[], b: readonly Incident[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!
    const y = b[i]!
    if (x.id !== y.id || x.title !== y.title || x.impact !== y.impact || x.status !== y.status) {
      return false
    }
  }
  return true
}

/**
 * Apply one server frame. Returns the *same* state object when the frame changed nothing, so a
 * subscriber can use identity as its "do I have work to do" test.
 */
export function applyMessage(state: LiveState, msg: ServerMsg): LiveState {
  const next = applyServer(state, msg)
  if (next === state) return state
  const counters = reconcile(state, next, msg.t === 'tick')
  const horde = reconcileHorde(state, next)
  if (counters === state.counters && horde === state.horde) return next
  return { ...next, counters, horde }
}

function applyServer(state: LiveState, msg: ServerMsg): LiveState {
  // docs/api.md § 1: "Clients must ignore frames received before `hello`."
  if (!state.helloReceived && msg.t !== 'hello') return state

  switch (msg.t) {
    case 'hello': {
      const squares: Record<ProviderId, SquareState> = { ...state.squares }
      const wire = msg.providers ?? {}
      for (const id of PROVIDER_IDS) {
        const snapshot = wire[id]
        const next = snapshot ? squareFromSnapshot(snapshot) : PEACEFUL
        squares[id] = sameSquare(state.squares[id], next) ? state.squares[id] : next
      }
      // `dps` is not in docs/api.md yet. It is read tolerantly rather than typed, so the drain can
      // follow a retuned server the day B24 puts it on the wire, without this client promising a
      // field the contract has not; a `hello` without it means the documented default.
      const dps = (msg as { dps?: unknown }).dps
      return {
        ...state,
        helloReceived: true,
        ver: count(msg.ver),
        all: count(msg.all),
        resetClicks: count(msg.reset?.c),
        lastReset: resetRecord(msg.reset?.last),
        squares,
        dps: typeof dps === 'number' && Number.isFinite(dps) && dps > 0 ? dps : DEFAULT_DPS,
      }
    }

    case 'tick': {
      // Only besieged providers appear under `p`; a square that stops appearing is not thereby
      // peaceful — that is what the `allclear` event and the next `hello` are for.
      let squares: Record<ProviderId, SquareState> | null = null
      for (const id of PROVIDER_IDS) {
        const counts = msg.p?.[id]
        if (!counts) continue
        const before = state.squares[id]
        const next: SquareState = {
          ...before,
          phase: 'siege',
          zombies: count(counts.z),
          clicks: count(counts.c),
          breaches: count(counts.bc),
          gateHp: gate(counts.gh, before.gateHp),
          walls: wallsFrom(counts.walls, before.walls),
        }
        if (sameSquare(before, next)) continue
        squares ??= { ...state.squares }
        squares[id] = next
      }
      const all = msg.all === undefined ? state.all : count(msg.all)
      const resetClicks = msg.r === undefined ? state.resetClicks : count(msg.r)
      if (squares === null && all === state.all && resetClicks === state.resetClicks) return state
      return { ...state, all, resetClicks, squares: squares ?? state.squares }
    }

    case 'siege': {
      if (!isProviderId(msg.p)) return state
      const next: SquareState = {
        phase: 'siege',
        sid: msg.sid,
        startedAt: msg.startedAt,
        lastEndedAt: state.squares[msg.p].lastEndedAt,
        down: true,
        incidents: Array.isArray(msg.incidents) ? msg.incidents : [],
        zombies: count(msg.z),
        clicks: 0,
        breaches: 0,
        gateHp: GATE_FULL,
        // The `siege` frame carries no `walls`; the first tick does, within 500 ms. Until then the
        // v2 page draws the honest start of a siege: three intact walls and nobody at them.
        walls: null,
      }
      return replaceSquare(state, msg.p, next)
    }

    /**
     * A wall down (`docs/api.md` § 1.2). The next wall starts at full health, or — after the
     * third — there is no current wall and `hp` is 0. Idempotent on `w`: a reconnect that replays
     * the event for a wall the page already knows fell must not append a fourth timestamp.
     */
    case 'wall': {
      if (!isProviderId(msg.p)) return state
      const before = state.squares[msg.p]
      if (before.phase !== 'siege') return state
      const prev = before.walls ?? INTACT_WALLS
      const w = Math.max(0, Math.min(3, Math.trunc(count(msg.w))))
      const z = Math.max(0, Math.trunc(count(msg.z)))
      if (w <= prev.w) {
        return replaceSquare(state, msg.p, {
          ...before,
          walls: { ...prev, z: Math.max(prev.z, z) },
        })
      }
      const fell = [...prev.fell]
      const post = [...prev.post]
      while (fell.length < w) {
        fell.push(typeof msg.at === 'string' ? msg.at : '')
        post.push(typeof msg.post === 'string' ? msg.post : null)
      }
      return replaceSquare(state, msg.p, {
        ...before,
        walls: { z, w, hp: w >= 3 ? 0 : 1000, fell, post },
      })
    }

    case 'allclear': {
      if (!isProviderId(msg.p)) return state
      // No celebration here — F17 owns that. All this does is stop the page claiming a siege
      // that the server has already ended, which is the whole point of the frame.
      const before = state.squares[msg.p]
      return replaceSquare(state, msg.p, {
        ...PEACEFUL,
        incidents: before.incidents,
        down: false,
        // The frame's own `endedAt`, not the clock: a tab that reconnects an hour later must not
        // claim the siege ended when its socket did.
        lastEndedAt: typeof msg.endedAt === 'string' ? msg.endedAt : before.lastEndedAt,
      })
    }

    case 'incidents': {
      if (!isProviderId(msg.p)) return state
      const before = state.squares[msg.p]
      return replaceSquare(state, msg.p, {
        ...before,
        down: msg.down === true,
        incidents: Array.isArray(msg.incidents) ? msg.incidents : [],
      })
    }

    case 'reset': {
      if (state.lastReset?.id === msg.id) return state
      const record = resetRecord({
        id: msg.id,
        kind: msg.kind,
        at: msg.at,
        summary: msg.summary,
        clicksTaken: msg.clicksTaken,
        ...(msg.url === undefined ? {} : { url: msg.url }),
      })
      return { ...state, lastReset: record, resetClicks: 0 }
    }

    /**
     * The receipt for one of our own batches — and the one frame whose *absence* is normal.
     *
     * Acks ride a droppable queue, `n` is what survived clipping and may be zero, and a batch
     * clipped to nothing produces no ack at all over the socket. So nothing here is gated on an
     * ack arriving, and nothing retries when one does not. What an ack is good for is the
     * authoritative numbers it carries, half a second before the tick that would repeat them:
     * `z` after the batch, which is what the danger meter and the crowd are drawn from, and `r`
     * for the reset counter, which no tick carries when only this tab is clicking it.
     *
     * `n` itself is deliberately unused. Subtracting it from `unacked` would double-count
     * against the tick that also contains those clicks, and `b` is left alone for the same
     * reason: the next tick's `bc` is absolute and arrives within 500 ms.
     */
    case 'ack': {
      if (msg.p === 'reset') {
        if (typeof msg.r !== 'number' || !Number.isFinite(msg.r)) return state
        return msg.r === state.resetClicks ? state : { ...state, resetClicks: msg.r }
      }
      if (!isProviderId(msg.p)) return state
      const before = state.squares[msg.p]
      if (before.phase !== 'siege' || typeof msg.z !== 'number' || !Number.isFinite(msg.z)) {
        return state
      }
      // `zw` is the v2 count after the batch — the same evidence as a tick's `walls.z`, half a
      // second early. Absent on a v1-only server, in which case the walls are left as they were.
      const zw = msg.zw
      const walls =
        typeof zw === 'number' && Number.isFinite(zw)
          ? { ...(before.walls ?? INTACT_WALLS), z: Math.max(0, Math.trunc(zw)) }
          : before.walls
      return replaceSquare(state, msg.p, { ...before, zombies: count(msg.z), walls })
    }

    case 'hb':
    case 'bye':
      // Connection-level, and handled by the transport.
      return state
  }
}

function replaceSquare(state: LiveState, id: ProviderId, next: SquareState): LiveState {
  if (sameSquare(state.squares[id], next)) return state
  return { ...state, squares: { ...state.squares, [id]: next } }
}

/** The worst thing happening anywhere, for the header line and the document title (F17). */
export function anySiege(state: LiveState): boolean {
  return PROVIDER_IDS.some((id) => state.squares[id].phase === 'siege')
}

/** The incident a square's pill should name: the most severe open one. */
export function leadIncident(square: SquareState): Incident | null {
  const rank: Record<string, number> = { critical: 3, major: 2, minor: 1 }
  let best: Incident | null = null
  for (const incident of square.incidents) {
    if (incident.status === 'resolved') continue
    if (best === null || (rank[incident.impact] ?? 0) > (rank[best.impact] ?? 0)) best = incident
  }
  return best
}

/** What the v2 odometer should render: the server's `walls.z` plus this tab's believed spawns. */
export function shownHorde(state: LiveState, id: ProviderId): number {
  return state.horde[id].shown
}
